/**
 * "Update ready" on a phone game board (web only): a strip in the map's tip
 * slot, styled like the tutorial tip, instead of a toast floating over the
 * map. Same rules as the toast (lib/webUpdate): never during your own turn,
 * and "Later" hides it until a newer build ships. Fades in.
 */
import React, { useEffect, useRef } from 'react'
import { Animated, Platform, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { UPDATE_TOAST_DESKTOP_MIN_WIDTH, refreshToLatest, setInlineUpdateSlot, snoozeUpdate, useWebUpdate } from '../../lib/webUpdate'
import type { TutorialStripEdge } from './TutorialHelpStrip'

const FADE_IN_MS = 280

export function WebUpdateStrip({ edge = 'top' }: { edge?: TutorialStripEdge }) {
  const { showToast } = useWebUpdate()
  const { width } = useWindowDimensions()
  const phone = Platform.OS === 'web' && width < UPDATE_TOAST_DESKTOP_MIN_WIDTH
  // Claim the update while this board is on a phone, so the toast steps aside.
  useEffect(() => {
    if (!phone) return
    setInlineUpdateSlot(true)
    return () => setInlineUpdateSlot(false)
  }, [phone])
  if (!phone || !showToast) return null
  return <StripBody edge={edge} />
}

function StripBody({ edge }: { edge: TutorialStripEdge }) {
  const opacity = useRef(new Animated.Value(0)).current
  useEffect(() => {
    Animated.timing(opacity, { toValue: 1, duration: FADE_IN_MS, useNativeDriver: false }).start()
  }, [opacity])
  return (
    <Animated.View testID="web-update-strip-fade" style={{ opacity }}>
      <View testID="web-update-strip" accessibilityRole="alert"
        className={`w-full flex-row items-center gap-2 ${edge === 'top' ? 'border-t' : 'border-b'} border-olive bg-canvas px-3 py-2`}>
        <MaterialCommunityIcons name="update" size={20} color="#991b1b" />
        <View className="flex-1 gap-0.5 justify-center">
          <Text className="font-stencil text-[11px] leading-[14px] uppercase text-blood">Update ready</Text>
          <Text className="font-typed text-xs leading-4 text-ink-soft">Refresh to get the latest build.</Text>
        </View>
        <TouchableOpacity testID="web-update-strip-later" accessibilityRole="button" accessibilityLabel="Later"
          onPress={snoozeUpdate} className="border-2 border-olive rounded-lg px-2.5 py-1.5">
          <Text className="text-ink font-stencil uppercase" style={{ fontSize: 10, letterSpacing: 0.6 }}>Later</Text>
        </TouchableOpacity>
        <TouchableOpacity testID="web-update-strip-refresh" accessibilityRole="button" accessibilityLabel="Refresh to update"
          onPress={() => refreshToLatest()} className="bg-blood rounded-lg px-2.5 py-1.5">
          <Text className="text-paper font-stencil uppercase" style={{ fontSize: 10, letterSpacing: 0.6 }}>Refresh</Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  )
}
